export function canDeleteMember(
  memberId: string,
  members: any,
  rootId: string,
): { canDelete: boolean; message?: string } {
  const member = members.find((m: { id: string }) => m.id === memberId);
  if (!member) return { canDelete: false, message: 'Member not found.' };

  if (member.id === rootId) {
    return {
      canDelete: false,
      message: 'This person started the tree and cannot be deleted.',
    };
  }

  // Build adjacency list (undirected graph)
  const graph: Record<string, Set<string>> = {};

  for (const m of members) {
    graph[m.id] = graph[m.id] || new Set();

    // Connect to parents
    if (m.father_id) {
      graph[m.id].add(m.father_id);
      graph[m.father_id] = graph[m.father_id] || new Set();
      graph[m.father_id].add(m.id);
    }
    if (m.mother_id) {
      graph[m.id].add(m.mother_id);
      graph[m.mother_id] = graph[m.mother_id] || new Set();
      graph[m.mother_id].add(m.id);
    }

    // Connect to children
    for (const childId of m.children_id) {
      graph[m.id].add(childId);
      graph[childId] = graph[childId] || new Set();
      graph[childId].add(m.id);
    }

    // Spouses
    for (const spouseId of m.spouse_id) {
      graph[m.id].add(spouseId);
      graph[spouseId] = graph[spouseId] || new Set();
      graph[spouseId].add(m.id);
    }

    // Siblings
    for (const siblingId of m.sibling_id) {
      graph[m.id].add(siblingId);
      graph[siblingId] = graph[siblingId] || new Set();
      graph[siblingId].add(m.id);
    }
  }

  // Remove the node from the graph
  delete graph[memberId];
  for (const node of Object.keys(graph)) {
    graph[node].delete(memberId);
  }

  // Traverse from root and count reachable nodes
  const visited = new Set<string>();
  const stack = [rootId];

  while (stack.length > 0) {
    const current = stack.pop()!;
    if (!visited.has(current)) {
      visited.add(current);
      for (const neighbor of Array.from(graph[current] || [])) {
        stack.push(neighbor);
      }
    }
  }

  // If not all other members are reachable, deletion splits the tree
  const remainingIds = members
    .filter((m: { id: string }) => m.id !== memberId)
    .map((m: { id: any }) => m.id);
  const unreachable = remainingIds.filter((id: string) => !visited.has(id));

  if (unreachable.length > 0) {
    return {
      canDelete: false,
      message:
        'This person cannot be deleted because that would split the tree. Try deleting outer people on the tree first.',
    };
  }

  return { canDelete: true };
}
